import { COMPONENT_TYPES } from "helpers/utils/constants";
import {
  combineDateAndTime,
  deleteValues,
  payloadBool,
  payloadFormatArray,
  payloadGetVal,
  payloadKeyVal,
  payloadKeyVals,
} from "helpers/utils/utilities";
import { isTrue } from "helpers/utils/validators";

export const maxFileSizeCheck = (
  winnerAchievements,
  achievementMedia,
  profileMedia
) => {
  let files = [];
  let totalSize = 0;
  for (const ach of winnerAchievements) {
    if (ach?.winner_achievement_image instanceof File) {
      files = [...files, ach?.winner_achievement_image];
    }
  }
  if (profileMedia instanceof File) files = [...files, profileMedia];

  if (achievementMedia instanceof File) files = [...files, achievementMedia];

  if (files?.length) {
    files.forEach((d) => {
      totalSize = totalSize + d?.size;
    });
    if (totalSize > 4 * 1024 * 1024) {
      return true;
    }
    return false;
  } else {
    return false;
  }
};

export const formChallengePayload = ({
  data,
  isDraft,
  editMode,
  addedSponsers,
  experts,
  customsDateMapping,
  criteria,
  selectedLab,
  selectedResource,
  winnerAchievements,
  socialLinks,
  taskValues,
  questionsValues,
}) => {
  const shouldDeleteWinnerAchievements = !Boolean(winnerAchievements.length);
  const salaryPaymentFrequencyMapping = { zero: 0, one: 1, two: 2, three: 3 };
  let payload = {
    ...data,
    automatic_alert: data?.automatic_alert ?? "day",
    request_type: isDraft ? "draft" : "publish",
    cover_banner_type: data?.iframe_code
      ? "embedded"
      : data?.cover_image instanceof File
      ? "image"
      : "none",
    cover_embedded: data?.iframe_code,
    labs: selectedLab
      .filter((item) => item.type === COMPONENT_TYPES.LAB)
      .map((item) => item.uuid),
    lab_programs: selectedLab
      .filter((item) => item.type === COMPONENT_TYPES.LAB_PROGRAMS)
      .map((item) => item.uuid),
    resource_modules: selectedResource
      .filter((item) => item.type === COMPONENT_TYPES.RESOURCE)
      .map((item) => item.uuid),
    resource_collections: selectedResource
      .filter((item) => item.type === COMPONENT_TYPES.RESOURCE_COLLECTION)
      .map((item) => item.uuid),
    resource_groups: selectedResource
      .filter((item) => item.type === COMPONENT_TYPES.RESOURCE_GROUP)
      .map((item) => item.uuid),
    assessment_title: payloadFormatArray(criteria, "title"),
    assessment_description: payloadFormatArray(criteria, "description"),
    assessment_score: payloadFormatArray(criteria, "score"),
    assessment_weight: payloadFormatArray(criteria, "weight"),

    expert_references: payloadFormatArray(experts, "email"),

    members_email: payloadFormatArray(data?.members_email, "email"),
    visibility: data?.visibility ? "users" : "hidden",
    achievement_participation: "participation",
    achievement_prize: data?.achievement_prize,
    external_links: payloadFormatArray(socialLinks, "url"),
    external_link_ids: payloadFormatArray(socialLinks, "type"),
    allow_submit_project: payloadBool(data?.allow_submit_project),
    requirement_program: payloadBool(data?.requirement_program),
    complete_education_program: payloadBool(data?.complete_education_program),
    complete_experience: payloadBool(data?.complete_experience),
    is_auto_created: "no",
    is_ai_created: "no",
    ...(data?.timeline_type === "flexible" && {
      custom_timelines_title: customsDateMapping.map(
        (item) => item?.title ?? ""
      ),
      custom_timelines_number: customsDateMapping.map(
        (item) => item.number ?? ""
      ),
      custom_timelines_duration: customsDateMapping.map(
        (item) => item.duration ?? ""
      ),
      custom_timelines_description: customsDateMapping.map(
        (item) => item.description ?? ""
      ),
      schedule_custom_notify: customsDateMapping.map((item) => item.notify),
      custom_announcement_type: customsDateMapping.map(
        (item) => item.custom_announcement_type ?? ""
      ),
      custom_announcement_title: customsDateMapping.map(
        (item) => item.custom_announcement_title ?? ""
      ),
      custom_announcement_description: customsDateMapping.map(
        (item) => item.custom_announcement_description ?? ""
      ),
      custom_announcement_duration: customsDateMapping.map(
        (item) => item.custom_announcement_duration ?? ""
      ),
      custom_announcement_number: customsDateMapping.map(
        (item) => item.custom_announcement_number ?? ""
      ),
    }),
    integrate_campus_connect: data?.campus_connect
      ? data?.integrate_campus_connect
      : "no",
    campus_connect_salary_payment_frequency:
      salaryPaymentFrequencyMapping[
        data?.campus_connect_salary_payment_frequency
      ],
    campus_connect_has_image_file:
      data?.campus_connect_story_image instanceof File ? true : false,
    template_type:
      data?.template_id?.key === "custom"
        ? "new"
        : data?.template_id?.key === "any" || data?.template_id?.key === 0
        ? "any"
        : "existing",
    task_questions: taskValues.map((item) => item.task),
    pitch_questions: questionsValues.map((item) => item.question),
    pitch_questions_description: questionsValues.map((item) => item.prompt),
    start_date: combineDateAndTime(data?.start_date, data?.start_date_time),

    registration_deadline_date: combineDateAndTime(
      data?.registration_deadline_date,
      data?.registration_deadline_time
    ),
    submission_deadline_date: combineDateAndTime(
      data?.submission_deadline_date,
      data?.submission_deadline_time
    ),
    self_assessment: data.self_assessment ? 1 : 0,
    is_achievement_enabled: payloadBool(data.is_achievement_enabled),
    is_pre_built_achievement: payloadBool(
      Boolean(data?.pre_built_achievement_id)
    ),
    is_winner_achievement_enabled: payloadBool(
      data?.is_winner_achievement_enabled
    ),
  };
  if (isTrue(payload.is_pre_built_achievement))
    payload["achievement_id"] = data.pre_built_achievement_id;

  if (isTrue(payload.is_winner_achievement_enabled)) {
    if (payload?.pre_built_winner_achievement_id?.length) {
      payload["is_pre_built_winner_achievement"] = payloadBool(true);
      payload["winner_achievement_ids"] =
        data["pre_built_winner_achievement_id"];
    } else {
      payload = {
        ...payload,
        is_pre_built_winner_achievement: "no",
        winner_achievement_name: payloadFormatArray(
          winnerAchievements,
          "winner_achievement_name"
        ).filter(Boolean),
        winner_achievement_points: payloadFormatArray(
          winnerAchievements,
          "winner_achievement_points"
        ).filter(Boolean),

        winner_achievement_image: payloadFormatArray(
          winnerAchievements,
          "winner_achievement_image"
        ).filter((d) => !!d && typeof d !== "string"),
        winner_achievement_prize: payloadFormatArray(
          winnerAchievements,
          "winner_achievement_prize"
        ).filter(Boolean),
        old_winner_achievement_image: payloadFormatArray(
          winnerAchievements,
          "winner_achievement_image"
        ).filter(Boolean),
      };
    }
  }

  if (
    data.flexible_expire_deadline === undefined ||
    data.flexible_expire_deadline === null
  ) {
    delete payload["flexible_expire_deadline"];
  }
  payloadKeyVal(payload, [
    "level_id",
    "category_id",
    "duration_id",
    "template_id",
  ]);

  payloadKeyVals(payload, [
    "skills",
    "tags",
    "skill_groups",
    "skill_stacks",
    "project_submission_requirement_ids",
    "host_id",
    "external_link_ids",
    "jobs",
  ]);

  payloadGetVal(payload, ["campus_connect_schools"]);
  if (addedSponsers.length) {
    payload.host_id = [
      ...payload.host_id,
      ...addedSponsers.map((item) => item.id),
    ];
  }
  const deleteVals = [
    "template",
    "campus_connect",
    "tags",
    "start_date_time",
    "registration_deadline_time",
    "submission_deadline_time",
    "iframe_code",
    "scorm_file",
    "scorm",
    "scorm_url",
    "pre_built_achievement_id",
    "pre_built_winner_achievement_id",
  ];
  if (data?.timeline_type === "flexible") {
    deleteVals.push(
      ...[
        "submission_deadline_date",
        "start_date",
        "registration_deadline_date",
      ]
    );
  }
  if (data?.template_id?.key === undefined || data?.template_id?.key === null) {
    deleteVals.push("template_type");
  }
  if (!data?.skill_groups || !data?.skill_groups.length) {
    deleteVals.push("skill_groups");
  }
  if (!data?.skill_stacks || !data?.skill_stacks.length) {
    deleteVals.push("skill_stacks");
  }
  if (shouldDeleteWinnerAchievements) {
    deleteVals.push(
      "winner_achievement_name",
      "winner_achievement_points",
      "winner_achievement_prize"
    );
  }
  if (data?.host_id === undefined) {
    deleteVals.push("host_id");
  }

  if (
    data?.template_id?.key === "custom" ||
    data?.template_id?.key === "any" ||
    data?.template_id?.key === 0
  ) {
    deleteVals.push("template_id");
  }

  if (data?.assessment_type === "none")
    deleteVals.push(
      "assessment_title",
      "assessment_description",
      "assessment_score",
      "assessment_weight"
    );
  if (data?.assessment_type !== "ai") {
    deleteVals.push("assessment_description");
  }

  if (editMode) {
    [
      "media",
      "status",
      "challenge_requirements",
      "id",
      "slug",
      "likes",
      "shares",
      "media_type",
      "liked",
      "member_count",
      "favourite",
      "category",
      "duration",
      "level",
      "user",
      "module_progress",
    ].forEach((d) => deleteVals.push(d));
    if (typeof data.cover_image === "string") {
      deleteVals.push("cover_image");
    }
    if (typeof data.achievement_image === "string") {
      deleteVals.push("achievement_image");
    }
    if (typeof data.attachments === "string") {
      deleteVals.push("attachments");
    }
  }
  // delete undefined or null
  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined || payload[key] === null) {
      delete payload[key];
    }
  }
  deleteValues(payload, deleteVals);

  return payload;
};
